import { CONTROLLER, METHOD } from 'common/config/endpoint.config';
import { ApiMethods } from 'common/enums/api-methods.enum';
import { SearchDTO } from 'common/models';
import { ApiConfig } from 'common/models/api-service/api-config.model';
import { CategoryDTO } from 'common/models/data/entity.model';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { EditSubCategoryDTO, LinkIndicatorDTO, SubCategoryDTO } from '../models/master-data.model';
import { IndicatorDTO } from 'common/models/data/indicator.model';
import { EntityType } from 'common/enums';
import { EMPTY_GUID } from 'common/constants/user';
import { ExportRequestDto } from '../models/export.model';

const masterDataApiConfig = {
    getCategoriesUrl: (
        pagedRequest: PagedRequest,
        entityId?: string,
        entityType?: string
    ): ApiConfig => {
        if (entityId) {
            return new ApiConfig(
                `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.CATEGORIES}?${
                    entityType == 'Indicator' ? 'IndicatorId=' : 'SubCategoryId='
                }${entityId}`,
                ApiMethods.POST,
                '',
                pagedRequest
            );
        } else {
            return new ApiConfig(
                `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.CATEGORIES}`,
                ApiMethods.POST,
                '',
                pagedRequest
            );
        }
    },
    getEntitiesByCategoryIdUrl: (categoryId: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.ENTITY_BY_CATEGORY_DETAILS}/${categoryId}`,
            ApiMethods.GET
        ),
    getEntitiesByHierarchyIdUrl: (hierarchyId: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.ENTITY_BY_HIERARCHY_DETAILS}/${hierarchyId}`,
            ApiMethods.GET
        ),
    getSearchUrl: (data: SearchDTO): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.SEARCH}`,
            ApiMethods.POST,
            '',
            data
        ),
    archiveCategoryUrl: (id: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.ARCHIVE}/${METHOD.MASTER_DATA.ARCHIVE_CATEGORY}/${id}`,
            ApiMethods.PUT
        ),
    archiveSubCategoryUrl: (id: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.ARCHIVE}/${METHOD.MASTER_DATA.ARCHIVE_SUBCATEGORY}/${id}`,
            ApiMethods.PUT
        ),
    archiveIndicatorUrl: (id: string, hierarchyId: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.ARCHIVE}/${METHOD.MASTER_DATA.ARCHIVE_INDICATOR}/${id}/${hierarchyId}`,
            ApiMethods.PUT
        ),
    editCategoryUrl: (data: CategoryDTO): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.EDIT_CATEGORY}`,
            ApiMethods.POST,
            '',
            data
        ),
    deleteCategoryUrl: (id: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.CATEGORY}/${id}`,
            ApiMethods.POST
        ),
    deleteSubCategoryUrl: (id: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.SUB_CATEGORY}/${id}`,
            ApiMethods.POST
        ),
    deleteIndicatorUrl: (id: string, hierarchyId: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.DELETE_INDICATOR}/${id}/${hierarchyId}`,
            ApiMethods.POST
        ),
    addSubCategoryUrl: (data: SubCategoryDTO): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.ADD_SUB_CATEGORY}`,
            ApiMethods.POST,
            '',
            data
        ),
    fetchSubCategoryUrl: (subCategoryId: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.GET_SUBCATEGORY}/${subCategoryId}`,
            ApiMethods.GET
        ),
    updateSubCategoryUrl: (data: EditSubCategoryDTO): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.EDIT_SUBCATEGORY}`,
            ApiMethods.POST,
            '',
            data
        ),
    addCategoryUrl: (data: CategoryDTO): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.ADD_CATEGORY}`,
            ApiMethods.POST,
            '',
            data
        ),
    getReviewStatusUrl: (): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.GET_REVIEW_STATUS}`,
            ApiMethods.GET
        ),
    moveSubCategory: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.MOVE_SUB_CATEGORY}`,
            ApiMethods.POST,
            'option=' + data.movementOption,
            data.inputData
        ),

    moveSubcategoryUnderNewCategory: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.MOVE_SUB_CATEGORY_UNDER_NEW_CATEGORY}`,
            ApiMethods.POST,
            'option=' + data.movementOption,
            data.inputData
        ),

    moveSubcategoryUnderNewSubcategory: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.MOVE_SUB_CATEGORY_UNDER_NEW_SUB_CATEGORY}`,
            ApiMethods.POST,
            'option=' + data.movementOption,
            data.inputData
        ),

    updateIndicatorUrl: (data: IndicatorDTO): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.EDIT_INDICATOR}`,
            ApiMethods.POST,
            '',
            data
        ),
    addIndicatorUrl: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.ADD_INDICATOR}`,
            ApiMethods.POST,
            '',
            data
        ),
    addIndicatorUnderNewSubCategoryUrl: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.ADD_INDICATOR_UNDER_NEW_SUBCATEGORY}`,
            ApiMethods.POST,
            '',
            data
        ),
    addIndicatorUnderNewCategoryUrl: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.ADD_INDICATOR_UNDER_NEW_CATEGORY}`,
            ApiMethods.POST,
            '',
            data
        ),
    linkIndicatorUrl: (data: LinkIndicatorDTO): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.LINK_INDICATOR}`,
            ApiMethods.POST,
            '',
            data
        ),
    moveIndicator: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.MOVE_INDICATOR}`,
            ApiMethods.POST,
            '',
            data.inputData
        ),

    moveIndicatorUnderNewCategory: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.MOVE_INDICATOR_UNDER_NEW_CATEGORY}`,
            ApiMethods.POST,
            '',
            data.inputData
        ),

    moveIndicatorUnderNewSubcategory: (data: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.MOVE_INDICATOR_UNDER_NEW_SUB_CATEGORY}`,
            ApiMethods.POST,
            '',
            data.inputData
        ),
    getDeleteFeasibility: (payload: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.GET_DELETE_FEASIBILITY}/${payload.id}?entityType=${payload.entityType}`,
            ApiMethods.GET
        ),
    getArchiveFeasibility: (payload: {
        id: string;
        hierarchyId: string;
        entityType: EntityType;
    }): ApiConfig => {
        if (payload.entityType === EntityType.Category) {
            return new ApiConfig(
                `${CONTROLLER.ARCHIVE}/${METHOD.ARCHIVE.GET_ARCHIVE_FEASIBILITY}/${payload.id}/${EMPTY_GUID}?entityType=${payload.entityType}`,
                ApiMethods.GET
            );
        }
        return new ApiConfig(
            `${CONTROLLER.ARCHIVE}/${METHOD.ARCHIVE.GET_ARCHIVE_FEASIBILITY}/${payload.id}/${payload.hierarchyId}?entityType=${payload.entityType}`,
            ApiMethods.GET
        );
    },
    getIsMovableSubCategory: (payload: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.IS_MOVABLE_SUB_Category}/${payload.id}`,
            ApiMethods.GET
        ),
    discardChangesUrl: (): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.DISCARD_CHANGES}`,
            ApiMethods.POST
        ),
    getCloneFeasibility: (payload: { id: string; entityType: EntityType }): ApiConfig => {
        if (payload.entityType === EntityType.Category) {
            return new ApiConfig(
                `${CONTROLLER.MASTER_DATA}/${METHOD.CLONE.GET_CLONE_FEASIBILITY}?sourceId=${payload.id}&&entityType=${payload.entityType}`,
                ApiMethods.GET
            );
        }
        return new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.CLONE.GET_CLONE_FEASIBILITY}?sourceId=${payload.id}&&entityType=${payload.entityType}`,
            ApiMethods.GET
        );
    },
    cloneEntity: (payload: any): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.CLONE_ENTITY}`,
            ApiMethods.POST,
            '',
            payload
        ),
    checkDuplicateIndicator: (payload: string): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.CHECK_INDICATOR_EXISTS}`,
            ApiMethods.POST,
            '',
            payload
        ),
    exportData: (payload: ExportRequestDto): ApiConfig =>
        new ApiConfig(
            `${CONTROLLER.MASTER_DATA}/${METHOD.MASTER_DATA.EXPORT_DATA}`,
            ApiMethods.POST,
            '',
            payload
        )
};

export default masterDataApiConfig;
